import { createServerFn } from '@tanstack/react-start';
import { and, count, desc, eq } from 'drizzle-orm';
import { z } from 'zod';
import { getLatestApplyAttempt } from '@/db/models';
import { applications, jobs } from '@/db/schema';
import { getDb } from '@/lib/db';
import { authMiddleware } from '@/lib/server-fn-auth';
import { transitionApp } from '@/lib/transitions';
import { rowIdSchema } from '@/server/validators';

/** Paginated applications list. Mirrors `GET /api/applications`. */
export const fetchApplications = createServerFn({ method: 'GET' })
  .middleware([authMiddleware])
  .inputValidator(
    z.object({
      page: z.number().int().min(1).default(1),
      pageSize: z.number().int().min(1).max(200).default(50),
      status: z.string().optional(),
    }),
  )
  .handler(async ({ data }) => {
    const db = getDb();
    const limit = data.pageSize;
    const offset = (data.page - 1) * limit;

    const where = data.status
      ? and(eq(applications.status, data.status))
      : undefined;

    const [countRow] = await db
      .select({ total: count() })
      .from(applications)
      .where(where)
      .limit(1);

    const rows = await db
      .select({
        company: jobs.company,
        cover_letter: applications.cover_letter,
        cover_letter_pdf_path: applications.cover_letter_pdf_path,
        created_at: applications.created_at,
        id: applications.id,
        job_id: applications.job_id,
        match_score: jobs.match_score,
        outcome: applications.outcome,
        resume_pdf_path: applications.resume_pdf_path,
        reviewed_at: applications.reviewed_at,
        salary_max: jobs.salary_max,
        salary_min: jobs.salary_min,
        status: applications.status,
        submitted_at: applications.submitted_at,
        tailored_resume_md: applications.tailored_resume_md,
        title: jobs.title,
        url: jobs.url,
      })
      .from(applications)
      .innerJoin(jobs, eq(jobs.id, applications.job_id))
      .where(where)
      .orderBy(desc(applications.created_at))
      .limit(limit)
      .offset(offset);

    return {
      applications: rows,
      limit,
      page: data.page,
      total: countRow?.total ?? 0,
    };
  });

/** Single application with its joined job detail. Mirrors `GET /api/applications/$id`. */
export const fetchApplication = createServerFn({ method: 'GET' })
  .middleware([authMiddleware])
  .inputValidator(z.object({ id: rowIdSchema }))
  .handler(async ({ data }) => {
    const db = getDb();
    const [row] = await db
      .select({
        company: jobs.company,
        cover_letter: applications.cover_letter,
        cover_letter_pdf_path: applications.cover_letter_pdf_path,
        created_at: applications.created_at,
        description: jobs.description,
        id: applications.id,
        job_id: applications.job_id,
        location: jobs.location,
        match_score: jobs.match_score,
        outcome: applications.outcome,
        resume_pdf_path: applications.resume_pdf_path,
        reviewed_at: applications.reviewed_at,
        salary_max: jobs.salary_max,
        salary_min: jobs.salary_min,
        source: jobs.source,
        status: applications.status,
        submitted_at: applications.submitted_at,
        tailored_resume_md: applications.tailored_resume_md,
        tech_stack: jobs.tech_stack,
        title: jobs.title,
        url: jobs.url,
      })
      .from(applications)
      .innerJoin(jobs, eq(jobs.id, applications.job_id))
      .where(eq(applications.id, data.id))
      .limit(1);

    if (!row) {
      throw new Error('Not found');
    }

    return row;
  });

/** Latest apply attempt for an application. Mirrors `GET /api/applications/$id/apply-attempt`. */
export const fetchLatestApplyAttempt = createServerFn({ method: 'GET' })
  .middleware([authMiddleware])
  .inputValidator(z.object({ applicationId: rowIdSchema }))
  .handler(
    async ({ data }) =>
      (await getLatestApplyAttempt(getDb(), data.applicationId)) ?? null,
  );

/** Approve an application (and sync the job to `approved`). Mirrors `POST /api/applications/$id/approve`. */
export const approveApplication = createServerFn({ method: 'POST' })
  .middleware([authMiddleware])
  .inputValidator(z.object({ id: rowIdSchema }))
  .handler(async ({ data }) => {
    const result = await transitionApp(
      getDb(),
      data.id,
      'approved',
      'approved',
    );

    if ('error' in result) {
      throw new Error(result.error);
    }

    return result;
  });

/** Reject an application (and sync the job to `rejected`). Mirrors `POST /api/applications/$id/reject`. */
export const rejectApplication = createServerFn({ method: 'POST' })
  .middleware([authMiddleware])
  .inputValidator(z.object({ id: rowIdSchema }))
  .handler(async ({ data }) => {
    const result = await transitionApp(
      getDb(),
      data.id,
      'rejected',
      'rejected',
    );

    if ('error' in result) {
      throw new Error(result.error);
    }

    return result;
  });

/** Record a post-submission outcome. Mirrors `POST /api/applications/$id/outcome`. */
export const setOutcome = createServerFn({ method: 'POST' })
  .middleware([authMiddleware])
  .inputValidator(
    z.object({
      id: rowIdSchema,
      outcome: z.enum(['interview', 'rejected', 'no_response']),
    }),
  )
  .handler(async ({ data }) => {
    const db = getDb();
    const [app] = await db
      .select({ id: applications.id })
      .from(applications)
      .where(eq(applications.id, data.id))
      .limit(1);

    if (!app) {
      throw new Error('Not found');
    }

    await db
      .update(applications)
      .set({ outcome: data.outcome })
      .where(eq(applications.id, data.id));

    return { ok: true as const };
  });
